/**
 * Signaturen aller Postfächer (22.09.2026).
 *
 * Die Mailvorlagen sind seit Migration 164 für beide Inhaber gemeinsam;
 * persönlich ist nur noch die Signatur. Welche unter einer Mail steht,
 * entscheidet das Postfach, aus dem gesendet wird — sendet Volker, steht
 * Volkers darunter. Hier lassen sich beide pflegen, auch die des anderen
 * (Benjamin pflegt Volkers).
 */
import { useState } from "react";
import { fehlerText } from "@vimaana/ui";

import { useMailSignaturen, useMailSignaturSpeichern, type MailPostfach } from "../../hooks/usePost";
import "../../styles/signaturen.css";

const BEISPIEL = "Benjamin Hermsdorf\n\nTel.: …\n…@vimaanagarten.de\n\nVimaana Garten\nWeimarische Straße 31\n01127 Dresden\n\nwww.vimaanagarten.de";

function SignaturEditor({ postfach }: { postfach: MailPostfach }) {
  const speichern = useMailSignaturSpeichern();
  const [wert, setWert] = useState<string | null>(null);
  const aktuell = wert ?? postfach.signatur;
  const geaendert = wert !== null && wert !== postfach.signatur;
  const id = `mail-signatur-${postfach.owner_id}`;
  return (
    <div className="k-signatur">
      <label htmlFor={id} className="k-signatur-kopf">
        <strong>{postfach.eigenes ? "Deine Signatur" : `Signatur von ${postfach.name}`}</strong>
        <span>{postfach.email}</span>
      </label>
      {!postfach.signatur && wert === null && (
        <p className="k-signatur-hinweis">
          Noch leer — Mails aus diesem Postfach enden bisher nach „Mit freundlichen Grüßen“ ohne Namen.
        </p>
      )}
      <textarea
        id={id} rows={7} value={aktuell} placeholder={BEISPIEL}
        onChange={(e) => setWert(e.target.value)}
      />
      <div className="k-signatur-leiste">
        <button
          type="button" className="vg-btn vg-btn-primary" disabled={!geaendert || speichern.isPending}
          onClick={() => speichern.mutate(
            { signatur: aktuell, owner_id: postfach.owner_id },
            { onSuccess: () => setWert(null) },
          )}
        >
          {speichern.isPending ? "Speichert …" : "Signatur speichern"}
        </button>
        {speichern.isSuccess && !geaendert && <span className="k-signatur-ok">Gespeichert.</span>}
        {speichern.error && <span className="k-signatur-fehler">{fehlerText(speichern.error)}</span>}
      </div>
    </div>
  );
}

export default function SignaturenPflege() {
  const q = useMailSignaturen();
  const postfaecher = q.data?.postfaecher ?? [];
  return (
    <div className="k-signaturen">
      <p className="k-signaturen-intro">
        Die Signatur steht unter jeder Mail aus Kontor (Angebote, Rechnungen, Nachrichten) — immer die des
        Postfachs, aus dem gesendet wird. Die Mailvorlagen darüber sind für alle gemeinsam.
      </p>
      {q.isLoading && <p className="k-signaturen-intro">Lädt …</p>}
      {q.error && <p className="k-signatur-fehler">{fehlerText(q.error)}</p>}
      {postfaecher.map((p) => <SignaturEditor key={p.owner_id} postfach={p} />)}
    </div>
  );
}
